/**
 * Quote Comparison View
 *
 * Side-by-side comparison of 2-3 selected quotes. Delegates the heavy
 * presentation work to `OffersCompareGrid` so the quotes surface stays
 * visually consistent with the Offers page. The quote-specific affordances
 * (header close button, accept-only flow with no "counter" or "ask Astor"
 * stubs) are layered on top.
 *
 * Issue #809 — Quote Lifecycle (Comparison View).
 */

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Divider,
  GlassCard,
  GlassIconButton,
  GlassTooltip,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useMemo } from 'react';
import { type OfferRecord, OffersCompareGrid, pickWinnerId } from '../../components/offers';
import { useAcceptQuote } from '../../lib/trpc/hooks';

interface Quote {
  id: string;
  caseId: string;
  caseName: string;
  lenderName: string;
  amount: number;
  rate: number;
  term: number;
  monthlyPayment: number;
  status: string;
  receivedAt: string;
  expiresAt: string;
  calculations?: Record<string, unknown>;
}

const ACCEPTABLE_QUOTE_STATUSES: ReadonlySet<string> = new Set(['pending', 'active', 'sent', 'viewed']);

interface QuoteComparisonViewProps {
  quotes: Quote[];
  onClose: () => void;
  onSelectQuote: (quoteId: string) => void;
}

function formatDeadline(date: string): string {
  return new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function mapQuoteToOffer(q: Quote): OfferRecord {
  const calc = q.calculations ?? {};
  return {
    id: q.id,
    shortId: `#${q.id.slice(0, 8)}`,
    lender: q.lenderName,
    lenderTone: 'id',
    loanId: q.caseId,
    amount: q.amount,
    rate: q.rate,
    termMonths: q.term,
    monthly: q.monthlyPayment,
    fees: typeof calc.total_closing_costs === 'number' ? (calc.total_closing_costs as number) : 0,
    originationDollars: typeof calc.origination_fee === 'number' ? (calc.origination_fee as number) : undefined,
    dscr: typeof calc.dscr === 'number' ? (calc.dscr as number) : undefined,
    ltvPercent: typeof calc.ltv === 'number' ? (calc.ltv as number) : undefined,
    status: ACCEPTABLE_QUOTE_STATUSES.has(q.status) ? 'pending' : 'rejected',
    deadline: q.expiresAt ? formatDeadline(q.expiresAt) : undefined,
  };
}

export default function QuoteComparisonView({ quotes, onClose, onSelectQuote }: QuoteComparisonViewProps) {
  const { t } = useI18n();
  const acceptMutation = useAcceptQuote();

  const offers = useMemo(() => quotes.map(mapQuoteToOffer), [quotes]);
  const winnerId = useMemo(() => pickWinnerId(offers), [offers]);

  const handleAccept = async (offer: OfferRecord) => {
    try {
      await acceptMutation.mutateAsync({ quoteId: offer.id });
    } catch (_err) {
      // Mutation error surfaces via parent toast / inline alert.
    }
  };

  const handleCounter = (offer: OfferRecord) => {
    onSelectQuote(offer.id);
  };

  const handleAsk = (offer: OfferRecord) => {
    onSelectQuote(offer.id);
  };

  return (
    <GlassCard neonAccent="cyan" animate sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('consumer.offers.compare.title')}
        </Typography>
        <GlassTooltip title={t('common.close')}>
          <GlassIconButton size="small" onClick={onClose} sx={{ minHeight: 44, minWidth: 44 }}>
            <CloseIcon />
          </GlassIconButton>
        </GlassTooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <OffersCompareGrid
        offers={offers}
        winnerId={winnerId}
        onAccept={handleAccept}
        onCounter={handleCounter}
        onAsk={handleAsk}
      />
    </GlassCard>
  );
}
